/**
 * AuditTable, sheet 44 callouts 4 and 13 (mission 10 WP17 T100; FR-018, FR-024; A.5, D.2, D.6; U26; style guide
 * §6, §7.7, §7.16, §9; research §1.27 "Sheet 44").
 *
 * Presentational: one dense row per event in the API's order (newest first), under six `<th scope="col">`
 * headings from register 37 (`auditTableHeadings.*`):
 * - the time in mono UTC inside `<time dateTime>`, its local time in a tooltip that opens on hover and on
 *   keyboard focus (the time is focusable; U26). The page states the UTC note once, never per row;
 * - the type by its register 37 label (`auditType.<type>`; 44/13, the full vocabulary);
 * - the target as recorded, in mono, truncated with its `title`;
 * - the details (`AuditDetail`, the recorded fields only);
 * - the IP and the user agent as recorded, blank where the event recorded none (FR-023 item 1).
 * Rows are Class 2: they stay after a run's Class 1 objects are deleted (44/4).
 */
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableSkeletonRows } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { AuditEvent } from '@/lib/api/audit'
import { Mono, date, isoDateTime, time } from '@/lib/format'
import { copyText } from '@/lib/provisional-copy'

import { AuditDetail, Recorded } from './AuditDetail'

const HEADINGS = [
  'auditTableHeadings.time',
  'auditTableHeadings.type',
  'auditTableHeadings.target',
  'auditTableHeadings.details',
  'auditTableHeadings.ip',
  'auditTableHeadings.userAgent',
] as const

const LOCAL = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZoneName: 'short',
})

/** The viewer's local time, with its zone named (the tooltip; U26). */
export function localTime(iso: string): string {
  return LOCAL.format(new Date(iso))
}

/** The UTC time in mono; hover or keyboard focus shows the local time. */
function When({ iso }: { iso: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <time
          dateTime={isoDateTime(iso)}
          tabIndex={0}
          className="rounded-sm font-mono text-body-sm focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
        >
          {date(iso)} {time(iso)}
        </time>
      </TooltipTrigger>
      <TooltipContent>{localTime(iso)}</TooltipContent>
    </Tooltip>
  )
}

function AuditRow({ event }: { event: AuditEvent }) {
  return (
    <TableRow dense data-audit-row={event.id}>
      <TableCell className="whitespace-nowrap align-top py-2">
        <When iso={event.at} />
      </TableCell>
      <TableCell className="whitespace-nowrap align-top py-2 text-body-sm">{copyText(`auditType.${event.type}`)}</TableCell>
      <TableCell className="max-w-[12rem] align-top py-2">
        {event.target === '' ? null : (
          <Mono title={event.target} className="block truncate text-body-sm">
            {event.target}
          </Mono>
        )}
      </TableCell>
      <TableCell className="min-w-[14rem] align-top">
        <AuditDetail event={event} />
      </TableCell>
      <TableCell className="whitespace-nowrap align-top py-2">
        <Recorded value={event.ip} mono />
      </TableCell>
      <TableCell className="max-w-[14rem] align-top py-2">
        <Recorded value={event.user_agent} />
      </TableCell>
    </TableRow>
  )
}

export interface AuditTableProps {
  events: readonly AuditEvent[]
  loading: boolean
  /** `auditEmpty`. */
  emptyText: string
}

export function AuditTable({ events, loading, emptyText }: AuditTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow dense className="hover:bg-transparent">
          {HEADINGS.map((key) => (
            <TableHead key={key}>{copyText(key)}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading ? (
          <TableSkeletonRows columns={HEADINGS.length} />
        ) : events.length === 0 ? (
          <TableRow>
            <TableCell colSpan={HEADINGS.length} className="text-center text-body text-navy-600">
              {emptyText}
            </TableCell>
          </TableRow>
        ) : (
          events.map((event) => <AuditRow key={event.id} event={event} />)
        )}
      </TableBody>
    </Table>
  )
}
